import React, { useMemo, useState } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    Brush
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-slate-800/90 text-white p-3 rounded-lg shadow-xl border border-slate-700 max-w-xs z-50 backdrop-blur-sm">
                <p className="font-bold text-amber-500 mb-2 border-b border-slate-600 pb-1">Rango: {label}</p>
                <div className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                        <span>Frecuencia:</span>
                        <span className="font-bold ml-3">{payload[0].value} mediciones</span>
                    </div>
                </div>
            </div>
        );
    }
    return null;
};

const ChartDistribucionErrores = ({ data }) => {
    // data is array of objects { ErrorVolumenNeto: number }
    const [showInfo, setShowInfo] = useState(false);

    const histogramData = useMemo(() => {
        if (!data || data.length === 0) return [];

        // Define bins
        const bins = [
            { label: '< -0.10%', max: -0.10, count: 0 },
            { label: '-0.10 a -0.05%', max: -0.05, count: 0 },
            { label: '-0.05 a 0.00%', max: 0, count: 0 },
            { label: '0.00 a 0.05%', max: 0.05, count: 0 },
            { label: '0.05 a 0.10%', max: 0.10, count: 0 },
            { label: '> 0.10%', max: Infinity, count: 0 },
        ];

        data.forEach(item => {
            const val = parseFloat(item.ErrorVolumenBruto);
            if (isNaN(val)) return;

            if (val < -0.10) bins[0].count++;
            else if (val < -0.05) bins[1].count++;
            else if (val < 0) bins[2].count++;
            else if (val < 0.05) bins[3].count++;
            else if (val < 0.10) bins[4].count++;
            else bins[5].count++;
        });

        return bins;
    }, [data]);

    return (
        <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-100 flex flex-col h-[450px] relative">
            <div className="mb-6 flex justify-between items-start">
                <div className="flex items-start gap-2">
                    <div>
                        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                            📈 Distribución de Errores
                        </h3>
                        <p className="text-sm text-slate-500 mt-1">
                            Histograma de Errores Volumétricos Brutos.
                            <span className="text-blue-500 text-xs ml-2">(Zoom disponible)</span>
                        </p>
                    </div>

                    <button
                        onClick={() => setShowInfo(!showInfo)}
                        className="mt-1 text-slate-400 hover:text-blue-600 transition-colors"
                        title="Ver explicación"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </button>

                    {showInfo && (
                        <div className="absolute top-14 left-4 z-50 bg-slate-800 text-white p-4 rounded-xl shadow-2xl border border-slate-700 w-72 animated fadeIn">
                            <div className="flex justify-between items-start mb-2">
                                <h4 className="font-bold text-amber-500 text-sm">Distribución de Frecuencia</h4>
                                <button onClick={() => setShowInfo(false)} className="text-slate-400 hover:text-white"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                            </div>
                            <p className="text-xs text-slate-300 leading-relaxed">
                                Agrupa las mediciones según su error porcentual en rangos predefinidos (Bins).
                            </p>
                            <p className="text-xs text-slate-300 mt-2">
                                Permite visualizar si el error tiende a ser positivo, negativo o está centrado en cero (Ideal).
                            </p>
                        </div>
                    )}
                </div>
                <div className="px-3 py-1 bg-purple-50 text-purple-700 text-xs font-bold rounded-full uppercase tracking-wider">
                    Ingeniería de Precisión
                </div>
            </div>

            <div className="flex-grow overflow-x-auto">
                <div className="min-w-[700px] h-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={histogramData}
                            margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
                        >
                            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                            <XAxis
                                dataKey="label"
                                tick={{ fontSize: 10, fill: "#64748b" }}
                                axisLine={{ stroke: '#e2e8f0' }}
                                tickLine={false}
                                dy={10}
                                interval={0} // Show all labels
                            />
                            <YAxis
                                tick={{ fontSize: 11, fill: "#64748b" }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                {histogramData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={index < 3 ? '#6366f1' : '#8b5cf6'} />
                                ))}
                            </Bar>
                            <Brush
                                dataKey="label"
                                height={30}
                                stroke="#64748b"
                                travellerWidth={10}
                                fill="#f8fafc"
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default ChartDistribucionErrores;
